import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { AnimatePresence, motion } from 'framer-motion';
import { Eye, EyeOff, Mail, Lock, User, Phone, ArrowRight, ArrowLeft } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import Button from '../../components/UI/Button';
import {
  AuthCard,
  AuthCardHeader,
  AuthField,
  AuthInputWrapper,
  authInputWithIconClass,
  AccountTypeCards,
  PasswordStrength,
  allPasswordRulesMet,
  AuthSuccessState,
  AuthAlert,
  AuthPageFrame,
  type AccountType,
} from '../../components/Auth';

const AUTH_ACCOUNT_TYPE_HINT_KEY = 'auth_account_type_hint';

interface RegisterFormData {
  fullName: string;
  email: string;
  phone?: string;
  password: string;
  confirmPassword: string;
  account_type: AccountType;
}

function splitFullName(fullName: string): { first_name: string; last_name: string } {
  const trimmed = fullName.trim();
  const space = trimmed.indexOf(' ');
  if (space === -1) return { first_name: trimmed, last_name: trimmed };
  return {
    first_name: trimmed.slice(0, space),
    last_name: trimmed.slice(space + 1).trim() || trimmed.slice(0, space),
  };
}

const Register: React.FC = () => {
  const [step, setStep] = useState<1 | 2 | 'success'>(1);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);
  const [registeredEmail, setRegisteredEmail] = useState('');
  const { register: registerUser, clearError } = useAuth();
  const navigate = useNavigate();

  const {
    register,
    handleSubmit,
    watch,
    setValue,
    trigger,
    formState: { errors },
  } = useForm<RegisterFormData>({
    defaultValues: { account_type: 'business' },
  });

  const accountType = watch('account_type');
  const password = watch('password') ?? '';
  const confirmPassword = watch('confirmPassword') ?? '';

  // Clear stale auth errors (e.g. failed login) when entering registration.
  useEffect(() => {
    clearError();
    setFormError(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const goToStep2 = async () => {
    setFormError(null);
    clearError();
    const valid = await trigger('account_type');
    if (valid) setStep(2);
  };

  const onSubmit = async (data: RegisterFormData) => {
    if (!allPasswordRulesMet(data.password)) {
      setFormError('Please meet all password requirements.');
      return;
    }
    if (data.password !== data.confirmPassword) {
      setFormError('Passwords do not match.');
      return;
    }

    try {
      setIsLoading(true);
      setFormError(null);
      clearError();

      const { first_name, last_name } = splitFullName(data.fullName);
      localStorage.setItem(AUTH_ACCOUNT_TYPE_HINT_KEY, data.account_type);

      await registerUser({
        email: data.email,
        password: data.password,
        first_name,
        last_name,
        account_type: data.account_type,
        ...(data.phone?.trim() && { phone: data.phone.trim() }),
      });

      setRegisteredEmail(data.email);
      setStep('success');
    } catch (err: unknown) {
      const axiosErr = err as {
        response?: { data?: { error?: { details?: { field: string; message: string }[]; message?: string }; message?: string } };
        message?: string;
      };
      if (axiosErr?.response?.data?.error?.details?.length) {
        setFormError(
          axiosErr.response.data.error.details.map((e) => `${e.field}: ${e.message}`).join(' ')
        );
      } else if (axiosErr?.response?.data?.error?.message) {
        setFormError(axiosErr.response.data.error.message);
      } else if (axiosErr?.response?.data?.message) {
        setFormError(axiosErr.response.data.message);
      } else if (err instanceof Error) {
        setFormError(err.message);
      } else {
        setFormError('Registration failed. Please check your information and try again.');
      }
    } finally {
      setIsLoading(false);
    }
  };

  const continueToVerify = () => {
    navigate(`/verify-email?email=${encodeURIComponent(registeredEmail)}`, {
      state: { email: registeredEmail },
    });
  };

  const displayError = formError;

  return (
    <AuthPageFrame>
      <AuthCard>
        <AnimatePresence mode="wait">
          {step === 'success' ? (
            <motion.div
              key="success"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
            >
              <AuthSuccessState
                title="Account created successfully"
                description={
                  <>
                    We&apos;ve sent a 6-digit verification code to{' '}
                    <span className="font-medium text-gray-800">{registeredEmail}</span>
                  </>
                }
                loadingMessage="Preparing your finance workspace..."
              >
                <Button
                  type="button"
                  variant="primary"
                  size="lg"
                  className="w-full rounded-xl"
                  onClick={continueToVerify}
                >
                  Verify email
                  <ArrowRight className="ml-2 h-4 w-4" aria-hidden />
                </Button>
              </AuthSuccessState>
            </motion.div>
          ) : (
            <motion.div
              key="form"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
            >
              <AuthCardHeader
                title="Create your account"
                subtitle={
                  step === 1
                    ? 'Step 1 of 2 — Select your workspace'
                    : 'Step 2 of 2 — Account details'
                }
              />

              <motion.div
                className="mb-4 flex gap-2"
                aria-hidden
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
              >
                {[1, 2].map((s) => (
                  <motion.div
                    key={s}
                    className="h-1 flex-1 rounded-full bg-gray-200"
                    animate={{
                      backgroundColor: (step === 2 ? 2 : step) >= s ? '#2563eb' : '#e5e7eb',
                    }}
                    transition={{ duration: 0.35 }}
                  />
                ))}
              </motion.div>

              <form onSubmit={handleSubmit(onSubmit)} className="space-y-4" noValidate>
                <AnimatePresence mode="wait">
                  {step === 1 ? (
                    <motion.div
                      key="step1"
                      initial={{ opacity: 0, x: 12 }}
                      animate={{ opacity: 1, x: 0 }}
                      exit={{ opacity: 0, x: -12 }}
                      transition={{ duration: 0.3 }}
                      className="space-y-4"
                    >
                      <input type="hidden" {...register('account_type', { required: true })} />
                      <AccountTypeCards
                        value={accountType}
                        onChange={(type) => setValue('account_type', type, { shouldValidate: true })}
                        error={errors.account_type?.message}
                      />
                      <Button
                        type="button"
                        variant="primary"
                        size="lg"
                        className="w-full rounded-xl"
                        onClick={goToStep2}
                      >
                        Continue
                        <ArrowRight className="ml-2 h-4 w-4" aria-hidden />
                      </Button>
                    </motion.div>
                  ) : (
                    <motion.div
                      key="step2"
                      initial={{ opacity: 0, x: 12 }}
                      animate={{ opacity: 1, x: 0 }}
                      exit={{ opacity: 0, x: -12 }}
                      transition={{ duration: 0.3 }}
                      className="space-y-3.5"
                    >
                      <AuthField label="Full name" htmlFor="fullName" error={errors.fullName?.message}>
                        <AuthInputWrapper hasError={!!errors.fullName}>
                          <div className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3">
                            <User className="h-4 w-4 text-gray-400 transition-colors group-focus-within:text-blue-500" aria-hidden />
                          </div>
                          <input
                            id="fullName"
                            type="text"
                            autoComplete="name"
                            placeholder="Jane Doe"
                            className={authInputWithIconClass(!!errors.fullName)}
                            {...register('fullName', {
                              required: 'Full name is required',
                              minLength: { value: 2, message: 'Enter at least 2 characters' },
                            })}
                          />
                        </AuthInputWrapper>
                      </AuthField>

                      <AuthField label="Email address" htmlFor="register-email" error={errors.email?.message}>
                        <AuthInputWrapper hasError={!!errors.email}>
                          <div className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3">
                            <Mail className="h-4 w-4 text-gray-400 transition-colors group-focus-within:text-blue-500" aria-hidden />
                          </div>
                          <input
                            id="register-email"
                            type="email"
                            autoComplete="email"
                            placeholder="you@company.com"
                            className={authInputWithIconClass(!!errors.email)}
                            {...register('email', {
                              required: 'Email is required',
                              pattern: {
                                value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                                message: 'Invalid email address',
                              },
                            })}
                          />
                        </AuthInputWrapper>
                      </AuthField>

                      <AuthField
                        label="Mobile number"
                        htmlFor="phone"
                        optional
                        error={errors.phone?.message}
                      >
                        <AuthInputWrapper hasError={!!errors.phone}>
                          <div className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3">
                            <Phone className="h-4 w-4 text-gray-400 transition-colors group-focus-within:text-blue-500" aria-hidden />
                          </div>
                          <input
                            id="phone"
                            type="tel"
                            autoComplete="tel"
                            placeholder="+91 98765 43210"
                            className={authInputWithIconClass(!!errors.phone)}
                            {...register('phone', {
                              validate: (value) => {
                                if (!value?.trim()) return true;
                                const digits = value.replace(/\D/g, '');
                                return digits.length >= 10 || 'Phone number must have at least 10 digits';
                              },
                            })}
                          />
                        </AuthInputWrapper>
                      </AuthField>

                      <AuthField label="Password" htmlFor="password" error={errors.password?.message}>
                        <AuthInputWrapper hasError={!!errors.password}>
                          <div className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3">
                            <Lock className="h-4 w-4 text-gray-400 transition-colors group-focus-within:text-blue-500" aria-hidden />
                          </div>
                          <input
                            id="password"
                            type={showPassword ? 'text' : 'password'}
                            autoComplete="new-password"
                            placeholder="Create a password"
                            className={`${authInputWithIconClass(!!errors.password)} pr-11`}
                            {...register('password', {
                              required: 'Password is required',
                              validate: (v) => allPasswordRulesMet(v) || 'Password does not meet requirements',
                            })}
                          />
                          <button
                            type="button"
                            className="absolute inset-y-0 right-0 flex items-center rounded-r-xl px-3 text-gray-400 transition-colors hover:text-gray-600"
                            onClick={() => setShowPassword(!showPassword)}
                            aria-label={showPassword ? 'Hide password' : 'Show password'}
                          >
                            {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                          </button>
                        </AuthInputWrapper>
                        <PasswordStrength
                          password={password}
                          showMatch
                          matchValue={confirmPassword}
                          className="mt-2"
                          alwaysShowRules={password.length > 0 || !!errors.password}
                          showUnmetErrors={!!errors.password}
                        />
                      </AuthField>

                      <AuthField
                        label="Confirm password"
                        htmlFor="confirmPassword"
                        error={errors.confirmPassword?.message}
                      >
                        <AuthInputWrapper hasError={!!errors.confirmPassword}>
                          <div className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3">
                            <Lock className="h-4 w-4 text-gray-400" aria-hidden />
                          </div>
                          <input
                            id="confirmPassword"
                            type={showConfirmPassword ? 'text' : 'password'}
                            autoComplete="new-password"
                            placeholder="Confirm your password"
                            className={`${authInputWithIconClass(!!errors.confirmPassword)} pr-11`}
                            {...register('confirmPassword', {
                              required: 'Please confirm your password',
                              validate: (v) => v === password || 'Passwords do not match',
                            })}
                          />
                          <button
                            type="button"
                            className="absolute inset-y-0 right-0 flex items-center rounded-r-xl px-3 text-gray-400 hover:text-gray-600"
                            onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                            aria-label={showConfirmPassword ? 'Hide password' : 'Show password'}
                          >
                            {showConfirmPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                          </button>
                        </AuthInputWrapper>
                      </AuthField>

                      {displayError ? (
                        <AuthAlert title="Registration failed">{displayError}</AuthAlert>
                      ) : null}

                      <div className="flex gap-3">
                        <Button
                          type="button"
                          variant="secondary"
                          size="lg"
                          className="rounded-xl"
                          onClick={() => setStep(1)}
                        >
                          <ArrowLeft className="mr-2 h-4 w-4" aria-hidden />
                          Back
                        </Button>
                        <Button
                          type="submit"
                          variant="primary"
                          size="lg"
                          loading={isLoading}
                          className="flex-1 rounded-xl"
                        >
                          Create account
                        </Button>
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>
              </form>

            </motion.div>
          )}
        </AnimatePresence>
      </AuthCard>
    </AuthPageFrame>
  );
};

export default Register;
